import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import pb, { apiCall } from '../services/pocketbase';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiAlertCircle,
  FiArrowLeft,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiExclamationTriangle,
  FiImage,
  FiSave,
  FiSettings,
  FiVideo,
  FiYoutube,
  FiZap
} = FiIcons;

const VALID_SCOPES = ['client_facing', 'bpas_internal_team'];

const getYouTubeVideoId = (value) => {
  try {
    const url = new URL(String(value || '').trim());
    const host = url.hostname.replace(/^www\./, '').toLowerCase();
    if (host === 'youtu.be') return url.pathname.split('/').filter(Boolean)[0] || '';
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (url.pathname === '/watch') return url.searchParams.get('v') || '';
      const parts = url.pathname.split('/').filter(Boolean);
      if (['embed', 'shorts', 'live'].includes(parts[0])) return parts[1] || '';
    }
  } catch {
    return '';
  }
  return '';
};

const VideoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const appContextId = !isEdit ? searchParams.get('appId') : '';
  const requestedScope = searchParams.get('scope');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apps, setApps] = useState([]);
  const [contextApp, setContextApp] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(!appContextId);
  const [smartSource, setSmartSource] = useState('');
  const [autoThumbnail, setAutoThumbnail] = useState('');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    video_url: '',
    thumbnail_url: '',
    transcript_url: '',
    visibility_scope: 'bpas_internal_team',
    workflow_status: 'draft',
    app_ids: [],
    distribution_channels: [],
    category: 'onboarding',
    client_audience_role: '',
    internal_audience_role: '',
    tags: [],
    keywords: '',
    duration_seconds: '',
    sort_order: ''
  });

  useEffect(() => {
    loadApps();
    if (isEdit) {
      loadVideo();
    } else if (appContextId) {
      loadSmartDefaults(appContextId);
    }
  }, [id, appContextId]);

  const loadApps = async () => {
    try {
      const result = await apiCall(() => pb.collection('apps').getFullList({
        sort: 'app_name',
        filter: 'is_active = true'
      }));
      setApps(result);
    } catch (loadError) {
      console.error('Error loading apps:', loadError);
    }
  };

  const loadSmartDefaults = async (appId) => {
    const safeId = String(appId || '').replace(/[^A-Za-z0-9]/g, '');
    if (!safeId) {
      setError('The selected App context is invalid.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const filter = `app_ids ?= "${safeId}"`;
      const [appRecord, latestResult, sortResult] = await Promise.all([
        apiCall(() => pb.collection('apps').getOne(safeId)),
        apiCall(() => pb.collection('videos').getList(1, 1, { filter, sort: '-updated' })),
        apiCall(() => pb.collection('videos').getList(1, 1, { filter, sort: '-sort_order,-updated' }))
      ]);

      const latest = latestResult.items?.[0] || null;
      const validRequestedScope = VALID_SCOPES.includes(requestedScope) ? requestedScope : '';
      const scope = validRequestedScope || latest?.visibility_scope || 'bpas_internal_team';
      const latestChannels = Array.isArray(latest?.distribution_channels) ? latest.distribution_channels : [];
      const channels = latestChannels.length
        ? latestChannels
        : scope === 'client_facing'
          ? ['in_app_training_page']
          : ['internal_sop_library'];
      const currentMaxSort = Number(sortResult.items?.[0]?.sort_order || 0);

      setContextApp(appRecord);
      setFormData(prev => ({
        ...prev,
        visibility_scope: scope,
        workflow_status: 'draft',
        app_ids: [safeId],
        distribution_channels: channels,
        category: latest?.category || 'onboarding',
        client_audience_role: scope === 'client_facing' ? latest?.client_audience_role || '' : '',
        internal_audience_role: scope === 'bpas_internal_team' ? latest?.internal_audience_role || '' : '',
        sort_order: currentMaxSort + 1
      }));
      setSmartSource(latest ? `Reused from “${latest.title || 'latest video'}”` : 'New App defaults applied');
    } catch (loadError) {
      console.error('Failed to prepare App video defaults:', loadError);
      setError('Could not prepare the App defaults. You can still enter the video manually.');
      setAdvancedOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const loadVideo = async () => {
    try {
      setLoading(true);
      const video = await apiCall(() => pb.collection('videos').getOne(id));
      setFormData({
        title: video.title || '',
        description: video.description || '',
        video_url: video.video_url || '',
        thumbnail_url: video.thumbnail_url || '',
        transcript_url: video.transcript_url || '',
        visibility_scope: video.visibility_scope || 'bpas_internal_team',
        workflow_status: video.workflow_status || 'draft',
        app_ids: video.app_ids || [],
        distribution_channels: video.distribution_channels || [],
        category: video.category || 'onboarding',
        client_audience_role: video.client_audience_role || '',
        internal_audience_role: video.internal_audience_role || '',
        tags: Array.isArray(video.tags) ? video.tags : [],
        keywords: video.keywords || '',
        duration_seconds: video.duration_seconds ?? '',
        sort_order: video.sort_order ?? ''
      });
    } catch (loadError) {
      console.error('Failed to load video data:', loadError);
      setError('Failed to load video data');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'visibility_scope') {
      setFormData(prev => ({
        ...prev,
        visibility_scope: value,
        client_audience_role: value === 'client_facing' ? prev.client_audience_role : '',
        internal_audience_role: value === 'bpas_internal_team' ? prev.internal_audience_role : '',
        distribution_channels: prev.distribution_channels.length
          ? prev.distribution_channels
          : value === 'client_facing'
            ? ['in_app_training_page']
            : ['internal_sop_library']
      }));
      setFormErrors(prev => ({ ...prev, client_audience_role: null, internal_audience_role: null, app_ids: null }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleVideoUrlChange = (event) => {
    const value = event.target.value;
    const youtubeId = getYouTubeVideoId(value);
    const generatedThumbnail = youtubeId ? `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg` : '';

    setFormData(prev => ({
      ...prev,
      video_url: value,
      thumbnail_url: generatedThumbnail && (!prev.thumbnail_url || prev.thumbnail_url === autoThumbnail)
        ? generatedThumbnail
        : prev.thumbnail_url
    }));
    setAutoThumbnail(generatedThumbnail);
    if (formErrors.video_url) setFormErrors(prev => ({ ...prev, video_url: null }));
    if (generatedThumbnail && formErrors.thumbnail_url) setFormErrors(prev => ({ ...prev, thumbnail_url: null }));
  };

  const handleTagsChange = (event) => {
    const tagsArray = event.target.value.split(',').map(tag => tag.trim()).filter(Boolean);
    setFormData(prev => ({ ...prev, tags: tagsArray }));
  };

  const handleMultiSelect = (event, field) => {
    const options = Array.from(event.target.selectedOptions, option => option.value);
    setFormData(prev => ({ ...prev, [field]: options }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errors = {};
    const isPublished = formData.workflow_status === 'published';

    if (!formData.title?.trim()) errors.title = 'Title is required';
    if (isPublished) {
      if (!formData.description?.trim()) errors.description = 'Description is required before publishing';
      if (!formData.video_url?.trim()) errors.video_url = 'Video URL is required before publishing';
      if (!formData.thumbnail_url?.trim()) errors.thumbnail_url = 'Thumbnail URL is required before publishing';
    }

    if (formData.visibility_scope === 'client_facing') {
      if (!formData.client_audience_role) errors.client_audience_role = 'Choose the client audience before publishing this content';
      if (!Array.isArray(formData.app_ids) || formData.app_ids.length === 0) errors.app_ids = 'Client-facing videos must be linked to at least one App';
    }

    if (formData.visibility_scope === 'bpas_internal_team' && !formData.internal_audience_role) {
      errors.internal_audience_role = 'Choose the internal audience for this content';
    }

    if (!formData.distribution_channels || formData.distribution_channels.length === 0) {
      errors.distribution_channels = 'At least one distribution channel is required';
    } else if (formData.distribution_channels.length > 4) {
      errors.distribution_channels = 'Maximum 4 distribution channels allowed';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!validate()) return;
    setLoading(true);

    try {
      const dataToSave = {
        title: formData.title.trim(),
        description: formData.description?.trim() || '',
        video_url: formData.video_url?.trim() || '',
        thumbnail_url: formData.thumbnail_url?.trim() || '',
        transcript_url: formData.transcript_url?.trim() || '',
        visibility_scope: formData.visibility_scope,
        workflow_status: formData.workflow_status,
        app_ids: Array.isArray(formData.app_ids) ? formData.app_ids : [],
        distribution_channels: Array.isArray(formData.distribution_channels) ? formData.distribution_channels : [],
        category: formData.category,
        client_audience_role: formData.visibility_scope === 'client_facing' ? formData.client_audience_role : '',
        internal_audience_role: formData.visibility_scope === 'bpas_internal_team' ? formData.internal_audience_role : '',
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        keywords: formData.keywords?.trim() || '',
        duration_seconds: formData.duration_seconds === '' ? null : Number.parseInt(formData.duration_seconds, 10),
        sort_order: formData.sort_order === '' ? null : Number.parseInt(formData.sort_order, 10)
      };

      if (isEdit) {
        await apiCall(() => pb.collection('videos').update(id, dataToSave));
      } else {
        await apiCall(() => pb.collection('videos').create(dataToSave));
      }

      navigate(appContextId ? `/apps/${appContextId}/setup` : '/videos');
    } catch (saveError) {
      const pocketBaseErrors = saveError?.data?.data || saveError?.response?.data || {};
      const fieldErrors = {};
      Object.entries(pocketBaseErrors).forEach(([field, fieldError]) => {
        fieldErrors[field] = fieldError?.message || (Array.isArray(fieldError) ? fieldError[0] : fieldError);
      });

      if (Object.keys(fieldErrors).length > 0) {
        setFormErrors(fieldErrors);
        setError('Could not save video. Please fix the fields highlighted below.');
      } else {
        setError(saveError?.message || 'Failed to save video');
      }
      setAdvancedOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const readiness = useMemo(() => {
    const checks = [
      Boolean(formData.title?.trim()),
      Boolean(formData.video_url?.trim()),
      Boolean(formData.thumbnail_url?.trim()),
      formData.visibility_scope === 'client_facing' ? Boolean(formData.client_audience_role) : Boolean(formData.internal_audience_role),
      Boolean(formData.distribution_channels?.length)
    ];
    if (formData.workflow_status === 'published') checks.push(Boolean(formData.description?.trim()));
    return { complete: checks.filter(Boolean).length, total: checks.length };
  }, [formData]);

  const inputClass = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all placeholder-slate-500';
  const labelClass = 'block text-sm font-medium text-slate-300 mb-2';
  const sectionClass = 'bg-slate-900 rounded-xl p-6 border border-slate-800 mb-6';
  const isPublished = formData.workflow_status === 'published';
  const backTarget = appContextId ? `/apps/${appContextId}/setup` : '/videos';

  return (
    <div className="max-w-4xl mx-auto pb-12">
      <div className="flex items-center justify-between mb-7 gap-4">
        <div className="flex items-center space-x-4 min-w-0">
          <button type="button" onClick={() => navigate(backTarget)} className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white">{isEdit ? 'Edit Video' : appContextId ? 'Add Training Video' : 'Add New Video'}</h1>
            {contextApp && <p className="text-sm text-slate-500 mt-1 truncate">For {contextApp.app_name}</p>}
          </div>
        </div>
        <button type="button" onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-lg flex items-center gap-2 font-medium disabled:opacity-50 shadow-lg shadow-blue-900/20">
          <SafeIcon icon={FiSave} className="w-4 h-4" />
          <span>{loading ? 'Saving...' : 'Save Video'}</span>
        </button>
      </div>

      {appContextId && contextApp && (
        <div className="mb-6 bg-blue-500/5 border border-blue-500/20 rounded-xl p-4 flex items-start gap-3">
          <SafeIcon icon={FiZap} className="w-5 h-5 text-blue-400 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-200">Smart defaults applied for {contextApp.app_name}</p>
            <p className="text-sm text-slate-500 mt-1">App assignment, scope, distribution, category and sort order are pre-filled. {smartSource && <span>{smartSource}.</span>}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-200 px-4 py-3 rounded-xl flex items-center mb-6">
          <SafeIcon icon={FiAlertCircle} className="w-5 h-5 mr-2 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className={sectionClass}>
          <div className="flex items-center justify-between gap-4 mb-5">
            <div>
              <h2 className="text-lg font-bold text-white">Video essentials</h2>
              <p className="text-sm text-slate-500 mt-1">Enter only what is unique to this video.</p>
            </div>
            <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-slate-800 text-slate-400">{readiness.complete}/{readiness.total} ready</span>
          </div>

          <div className="space-y-5">
            <div>
              <label className={labelClass}>Video URL {isPublished ? '*' : ''}</label>
              <div className="relative">
                <SafeIcon icon={FiYoutube} className="absolute left-3 top-3 text-slate-500" />
                <input name="video_url" value={formData.video_url} onChange={handleVideoUrlChange} className={`${inputClass} pl-10`} placeholder="Paste YouTube, Vimeo, Dadan or other hosted video URL" />
              </div>
              {autoThumbnail && formData.thumbnail_url === autoThumbnail && <p className="text-xs text-blue-400 mt-1.5 flex items-center gap-1"><SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> YouTube thumbnail generated automatically</p>}
              {formErrors.video_url && <p className="text-red-400 text-sm mt-1">{formErrors.video_url}</p>}
            </div>

            <div>
              <label className={labelClass}>Video Title *</label>
              <input name="title" value={formData.title} onChange={handleChange} className={inputClass} placeholder="What will the user learn?" />
              {formErrors.title && <p className="text-red-400 text-sm mt-1">{formErrors.title}</p>}
            </div>

            <div>
              <label className={labelClass}>Description {isPublished ? '*' : ''}</label>
              <textarea name="description" value={formData.description} onChange={handleChange} rows="3" className={inputClass} placeholder="Short practical description of what this video covers" />
              {formErrors.description && <p className="text-red-400 text-sm mt-1">{formErrors.description}</p>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>Audience *</label>
                {formData.visibility_scope === 'client_facing' ? (
                  <select name="client_audience_role" value={formData.client_audience_role} onChange={handleChange} className={inputClass}>
                    <option value="">Select audience</option>
                    <option value="end_user">End User</option>
                    <option value="tenant_admin">Tenant Admin</option>
                  </select>
                ) : (
                  <select name="internal_audience_role" value={formData.internal_audience_role} onChange={handleChange} className={inputClass}>
                    <option value="">Select audience</option>
                    <option value="bpas_ops">Ops</option>
                    <option value="bpas_support">Support</option>
                    <option value="bpas_sales">Sales</option>
                    <option value="bpas_admin">Admin</option>
                  </select>
                )}
                {(formErrors.client_audience_role || formErrors.internal_audience_role) && <p className="text-red-400 text-sm mt-1">{formErrors.client_audience_role || formErrors.internal_audience_role}</p>}
              </div>

              <div>
                <label className={labelClass}>Status *</label>
                <select name="workflow_status" value={formData.workflow_status} onChange={handleChange} className={inputClass}>
                  <option value="draft">Draft</option>
                  <option value="review">Review</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <label className={labelClass}>Thumbnail URL {isPublished ? '*' : ''}</label>
              <div className="relative">
                <SafeIcon icon={FiImage} className="absolute left-3 top-3 text-slate-500" />
                <input name="thumbnail_url" value={formData.thumbnail_url} onChange={handleChange} className={`${inputClass} pl-10`} placeholder="Auto-filled for YouTube, otherwise paste thumbnail URL" />
              </div>
              {formErrors.thumbnail_url && <p className="text-red-400 text-sm mt-1">{formErrors.thumbnail_url}</p>}
            </div>
          </div>
        </div>

        <button type="button" onClick={() => setAdvancedOpen(current => !current)} className="w-full mb-6 px-5 py-4 rounded-xl border border-slate-800 bg-slate-900 hover:bg-slate-800/70 flex items-center justify-between text-left">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-800 text-blue-400"><SafeIcon icon={FiSettings} className="w-4 h-4" /></div>
            <div>
              <p className="text-sm font-semibold text-white">Advanced options</p>
              <p className="text-xs text-slate-500 mt-0.5">Targeting, distribution and metadata — normally pre-filled.</p>
            </div>
          </div>
          <SafeIcon icon={advancedOpen ? FiChevronUp : FiChevronDown} className="w-5 h-5 text-slate-500" />
        </button>

        {advancedOpen && (
          <>
            <div className={sectionClass}>
              <h3 className="text-lg font-semibold text-white mb-4 border-b border-slate-800 pb-2">Targeting & Distribution</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelClass}>Visibility Scope *</label>
                  <select name="visibility_scope" value={formData.visibility_scope} onChange={handleChange} className={inputClass}>
                    <option value="bpas_internal_team">Internal Team</option>
                    <option value="client_facing">Client Facing</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Category *</label>
                  <select name="category" value={formData.category} onChange={handleChange} className={inputClass}>
                    <option value="onboarding">Onboarding</option>
                    <option value="feature">Feature</option>
                    <option value="troubleshooting">Troubleshooting</option>
                    <option value="sop">SOP</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Apps {formData.visibility_scope === 'client_facing' ? '*' : ''}</label>
                  {apps.length === 0 ? (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-200 px-4 py-3 rounded-xl flex items-center">
                      <SafeIcon icon={FiExclamationTriangle} className="w-5 h-5 mr-2 flex-shrink-0" />
                      <div><p className="font-medium">No active Apps found</p><p className="text-sm">Add or activate an App first.</p></div>
                    </div>
                  ) : (
                    <select multiple name="app_ids" value={formData.app_ids} onChange={(event) => handleMultiSelect(event, 'app_ids')} className={`${inputClass} h-28`}>
                      {apps.map(app => <option key={app.id} value={app.id}>{app.app_name}</option>)}
                    </select>
                  )}
                  {formErrors.app_ids && <p className="text-red-400 text-sm mt-1">{formErrors.app_ids}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className={labelClass}>Distribution Channels *</label>
                  <select multiple name="distribution_channels" value={formData.distribution_channels} onChange={(event) => handleMultiSelect(event, 'distribution_channels')} className={`${inputClass} h-28`}>
                    <option value="in_app_training_page">In-App Training Page</option>
                    <option value="support_resolution">Support Resolution</option>
                    <option value="onboarding_modal">Onboarding Modal</option>
                    <option value="internal_sop_library">Internal SOP Library</option>
                  </select>
                  {formErrors.distribution_channels && <p className="text-red-400 text-sm mt-1">{formErrors.distribution_channels}</p>}
                </div>
              </div>
            </div>

            <div className={sectionClass}>
              <h3 className="text-lg font-semibold text-white mb-4 border-b border-slate-800 pb-2">Optional Metadata</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>Transcript URL</label>
                  <input name="transcript_url" value={formData.transcript_url} onChange={handleChange} className={inputClass} placeholder="Optional transcript link" />
                </div>
                <div>
                  <label className={labelClass}>Tags</label>
                  <input type="text" value={formData.tags.join(', ')} onChange={handleTagsChange} className={inputClass} placeholder="onboarding, dashboard" />
                </div>
                <div>
                  <label className={labelClass}>Keywords</label>
                  <input name="keywords" value={formData.keywords} onChange={handleChange} className={inputClass} placeholder="Optional search keywords" />
                </div>
                <div>
                  <label className={labelClass}>Duration (seconds)</label>
                  <input type="number" min="0" name="duration_seconds" value={formData.duration_seconds} onChange={handleChange} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Sort Order</label>
                  <input type="number" name="sort_order" value={formData.sort_order} onChange={handleChange} className={inputClass} />
                  {appContextId && <p className="text-xs text-slate-500 mt-1">Calculated automatically from this App's current videos.</p>}
                </div>
              </div>
            </div>
          </>
        )}

        {isPublished && readiness.complete < readiness.total && (
          <div className="mb-6 bg-yellow-500/10 border border-yellow-500/20 text-yellow-200 px-4 py-3 rounded-xl flex items-start gap-2 text-sm">
            <SafeIcon icon={FiExclamationTriangle} className="w-5 h-5 flex-shrink-0" />
            <span>Published content still has incomplete required information. Complete the highlighted essentials before saving.</span>
          </div>
        )}
      </form>
    </div>
  );
};

export default VideoForm;